import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IdObject } from 'src/app/core/id-object';
import { environment } from 'src/environments/environment';
import { Deposit, DepositDraft, DepositSearchData } from './deposit';
import { Item } from '../item/item';
import Compressor from 'compressorjs';
import { PagedResponse, Paging, PagingHeader } from '../../core/paging/paging';

@Injectable({
  providedIn: 'root',
})
export class DepositService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}deposit`;
  private static singleUrl = (apiUrl: string, id: string): string => `${apiUrl}deposit/${id}`;
  private static searchUrl = (apiUrl: string): string => `${apiUrl}deposit/search`;
  private static scanUrl = (apiUrl: string, isIdCardFromNetherlands: boolean): string => `${apiUrl}deposit/scan` + (isIdCardFromNetherlands ? '?country=nl' : '');

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
  }

  public getList(): Observable<Deposit[]> {
    const {apiUrl} = environment;
    const url = DepositService.listUrl(apiUrl);

    return this.httpClient
      .get<Deposit[]>(url)
      .pipe(catchError(this.handleError<Deposit[]>('DepositService->getList', [])));
  }

  public getSingle(id: string): Observable<Deposit> {
    const {apiUrl} = environment;
    const url = DepositService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<Deposit>(url)
      .pipe(catchError(this.handleError<Deposit>('DepositService->getSingle')));
  }

  public create(depositDraft: DepositDraft): Observable<string> {
    const {apiUrl} = environment;
    const url = DepositService.listUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.post<IdObject>(url, depositDraft, {headers}).pipe(
      map((idObject: IdObject) => idObject.id),
      catchError(this.handleError<string>('DepositService->create')),
    );
  }

  public patch(id: string, depositDraft: DepositDraft): Observable<boolean> {
    const {apiUrl} = environment;
    const url = DepositService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.patch<void>(url, depositDraft, {headers}).pipe(
      map(() => true),
      catchError(this.handleError<boolean>('DepositService->patch', false)),
    );
  }

  public remove(id: string): Observable<boolean> {
    const {apiUrl} = environment;
    const url = DepositService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('DepositService->remove', false)),
    );
  }

  public search(
    depositSearchData: DepositSearchData,
    paging?: Paging,
  ): Promise<PagedResponse<Deposit[]>> {
    const {apiUrl} = environment;
    const url = DepositService.searchUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    const params = paging ? new HttpParams()
      .set('page', paging.currentPage)
      .set('sort', paging.sort)
      .set('sortDirection', paging.sortDirection)
      .set('perPage', paging.perPage) : undefined;

    return new Promise(async resolve => {
      this.httpClient
        .post<Deposit[]>(
          url,
          depositSearchData,
          {params, headers, observe: 'response'},
        )
        .pipe(catchError(this.handleError<HttpResponse<Deposit[]>>(
          'DepositService->search',
          undefined,
        )))
        .subscribe((r) => {
          console.log('DONE - Response:', r.body);
          if (r.body) {
            const pagingHeader: PagingHeader = this.getPaging(r.headers);
            resolve({pagingHeader, items: r.body});
          }
        });
    });
  }

  getPaging(headers: HttpHeaders): PagingHeader {
    if (headers && headers.has('pagination-countavailable')) {
      const totalItems = headers.get('pagination-count');
      const perPage = headers.get('pagination-limit');
      const currentPage = headers.get('pagination-page');
      const hasNextPage = headers.get('pagination-nextpage');

      if (totalItems && perPage && currentPage && hasNextPage) {
        return {
          totalItems: parseInt(totalItems),
          perPage: parseInt(perPage),
          currentPage: parseInt(currentPage),
          hasNextPage: hasNextPage === 'true',
        };
      }
    }

    return {
      totalItems: 0, perPage: -1, currentPage: 0, hasNextPage: true,
    };
  }

  public scanImage(fileList: FileList, isIdCardFromNetherlands: boolean): Promise<Deposit> {
    const {apiUrl} = environment;
    const url = DepositService.scanUrl(apiUrl, isIdCardFromNetherlands);
    console.log("url", url)
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return new Promise(async resolve => {

      let base64: string = await this.convertFileToBase64(fileList.item(0)!) as string;
      let transferData = {
        file: base64.split(',')[1],
      };

      this.httpClient
        .post<Deposit>(url, transferData, {headers})
        .pipe(catchError(this.handleError<Deposit>('ItemService->scanImage', undefined)))
        .subscribe((deposit) => {
          resolve(deposit);
        });
    });
  }

  private async convertFileToBase64(file: File) {
    let imageCompressor: Promise<Blob> = new Promise(resolve => {
      new Compressor(file, {
        maxHeight: 3000,
        maxWidth: 3000,
        checkOrientation: true,
        quality: 0.9,
        success(result: Blob) {
          resolve(result);
        },
      });
    });

    const blobToBase64 = (blob: Blob) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise(resolve => {
        reader.onloadend = () => {
          resolve(reader.result);
        };
      });
    };

    return await blobToBase64(await imageCompressor);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      this.logger.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
